import { createContext, ReactNode, useContext, useMemo } from "react";

import { useFarmingInfos } from "./ecosystem";
import { ActionType, FarmingInfoExpanded } from "../types";

type FarmingInfosContextType = {
    ecosystems: FarmingInfoExpanded[] | undefined;
    isLoading: boolean;
};

const FarmingInfosContext = createContext<FarmingInfosContextType | undefined>(undefined);

export function FarmingInfosProvider({ children }: { children: ReactNode }) {
    const farmingInfos = useFarmingInfos();
    return <FarmingInfosContext.Provider value={farmingInfos}>{children}</FarmingInfosContext.Provider>;
}

export function useFarmingInfosContext(): FarmingInfosContextType {
    const context = useContext(FarmingInfosContext);
    if (!context) {
        throw new Error("useFarmingInfosContext must be used within a FarmingInfosProvider");
    }
    return context;
}

export function useFarmingInfoByActionTypeAndAddress(address: string | undefined, actionType: ActionType) {
    const { ecosystems } = useFarmingInfosContext();

    const farmingInfo = useMemo(() => {
        return getFarmingInfoByActionTypeAndAddress(address, actionType, ecosystems);
    }, [address, ecosystems, actionType]);

    return {
        farmingInfo,
        hasFarmingInfo: !!farmingInfo
    };
}

function getFarmingInfoByActionTypeAndAddress(
    address: string | undefined,
    actionType: ActionType,
    ecosystems: FarmingInfoExpanded[] | undefined
): FarmingInfoExpanded | undefined {
    if (!address || !ecosystems) return undefined;

    for (const fi of ecosystems) {
        // Check if this integration matches the address
        if (fi.integrationId !== address) {
            continue;
        }

        // Check if it has the requested action type by checking integrationType
        if (fi.integrationType === actionType) return fi;
    }

    return undefined;
}
